import dayjs from 'dayjs';

// Business days per month for rate conversion
export const BUSINESS_DAYS_PER_MONTH = 20;

// CAPEX threshold
export const CAPEX_THRESHOLD = 500000;

// Month names for display
export const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Map default roles to function categories
export const roleToFunctionMap = {
  "Product": "Product",
  "Tech Lead": "Other",
  "BA": "BA",
  "UX": "UX",
  "FE": "Developer",
  "BE": "Developer",
  "QA": "QA",
  "Other": "Other"
};

// Map default roles to developer focus (for Developer function)
export const roleToFocusMap = {
  "FE": "Frontend",
  "BE": "Backend",
  "Other": "Fullstack"
};

// Helper function to format month display
export const formatMonthDisplay = (month) => {
  if (month >= 1 && month <= 12) {
    return `${month} (${MONTH_NAMES[month - 1]})`;
  }
  return month;
};

// Helper function to get outcome timeline display
export const getOutcomeTimelineDisplay = (startMonth, adjustedMonths) => {
  if (!startMonth || !adjustedMonths) return '';
  
  const startDate = dayjs(startMonth + '-01');
  const endDate = startDate.add(adjustedMonths - 1, 'month');
  
  return `${startDate.format('MMM YYYY')} - ${endDate.format('MMM YYYY')} (${adjustedMonths} months)`;
};

// Helper function to get actual start/end dates for display
export const getActualDateDisplay = (startMonth, projectStartMonth, month) => {
  if (!startMonth || !projectStartMonth) return `Month ${month}`;
  
  const projectStart = dayjs(projectStartMonth + '-01');
  const actualDate = projectStart.add(month - 1, 'month');
  
  return actualDate.format('MMM YYYY');
};

// Helper function to get effective date ranges for a role
export const getEffectiveDateRanges = (role, headerStartMonth, headerEndMonth) => {
  if (role.useDefaultTimeline !== false) {
    // Use default timeline - always starts from month 1 of project
    return [{ 
      startMonth: 1, 
      endMonth: headerEndMonth, 
      allocation: role.defaultAllocation || 100 
    }];
  } else {
    // Use custom date ranges - validate they exist
    const customRanges = role.customDateRanges || [];
    if (customRanges.length === 0) {
      // Fallback to default if no custom ranges
      return [{ 
        startMonth: 1, 
        endMonth: headerEndMonth, 
        allocation: 100 
      }];
    }
    return customRanges;
  }
};

// Updated ensureRoleFields to use new structure
export const ensureRoleFields = (roles, headerEndMonth = 6) =>
  (roles || []).map(role => {
    const effectiveRanges = getEffectiveDateRanges(role, 1, headerEndMonth);
    return {
      ...role,
      id: role.id || `${role.name}-${Date.now()}-${Math.random()}`,
      roster_member_id: role.roster_member_id ?? "",
      name: role.name,
      company: role.company || "CA",
      location: role.location || "US",
      function: role.function || roleToFunctionMap[role.name] || "Other",
      focus: role.function === "Developer" ? (role.focus || roleToFocusMap[role.name] || "Fullstack") : undefined,
      role: role.role || "",
      rate: Math.round(role.rate || 0),
      notes: role.notes || "",
      // New timeline structure
      useDefaultTimeline: role.useDefaultTimeline !== false, // Default to true
      defaultAllocation: role.defaultAllocation || role.allocation || 100,
      customDateRanges: role.customDateRanges || [],
      // Update legacy dateRanges for backward compatibility
      dateRanges: effectiveRanges,
      // Add import tracking
      importedFromCostEstimation: role.importedFromCostEstimation || false
    };
  });

// Calculate total current role costs
export function calculateTotalCurrentRoleCosts(data) {
  if (!data || !data.roles) return 0;
  
  return Math.round(data.roles.reduce((sum, role) => {
    const effectiveRanges = getEffectiveDateRanges(role, 1, data.adjustedMonths);
    
    const totalMonths = effectiveRanges.reduce((months, dateRange) => {
      const startMonth = Math.max(1, dateRange.startMonth || 1);
      const endMonth = Math.min(data.adjustedMonths, dateRange.endMonth || data.adjustedMonths);
      const duration = Math.max(0, endMonth - startMonth + 1);
      const allocation = (dateRange.allocation || 100) / 100;
      
      return months + (duration * allocation);
    }, 0);
    
    return sum + (role.rate || 0) * totalMonths;
  }, 0));
}

// Calculate monthly breakdown
export const calculateMonthlyBreakdown = (formData, fixedCosts) => {
  const months = formData.adjustedMonths || 6;
  const breakdown = {
    fixedCosts: Array(months).fill(0),
    roles: {},
    monthlyTotals: Array(months).fill(0),
    developmentTeamTotals: Array(months).fill(0)
  };

  // Fixed costs calculation
  (fixedCosts || []).forEach(cost => {
    const startMonth = Math.max(1, cost.startMonth || 1);
    const endMonth = Math.min(months, cost.endMonth || months);
    const monthlyCost = Math.round(cost.monthlyCost || 0);

    for (let i = startMonth - 1; i < endMonth; i++) {
      if (i >= 0 && i < months) {
        breakdown.fixedCosts[i] += monthlyCost;
        breakdown.monthlyTotals[i] += monthlyCost;
      }
    }
  });

  // Roles calculation
  (formData.roles || []).forEach(role => {
    const functionName = role.function || 'Unknown Function';
    if (!breakdown.roles[functionName]) {
      breakdown.roles[functionName] = Array(months).fill(0);
    }

    const effectiveRanges = getEffectiveDateRanges(role, 1, formData.adjustedMonths);
    
    effectiveRanges.forEach(dateRange => {
      const startMonth = Math.max(1, dateRange.startMonth || 1);
      const endMonth = Math.min(months, dateRange.endMonth || months);
      const allocation = (dateRange.allocation || 100) / 100;
      const monthlyRate = Math.round((role.rate || 0) * allocation);

      // Apply costs to each month in the range
      for (let i = startMonth - 1; i < endMonth; i++) {
        if (i >= 0 && i < months) {
          breakdown.roles[functionName][i] += monthlyRate;
          breakdown.developmentTeamTotals[i] += monthlyRate;
          breakdown.monthlyTotals[i] += monthlyRate;
        }
      }
    });
  });

  return breakdown;
};

// Convert daily rate to monthly rate
export const convertDailyToMonthly = (dailyRate, annualWorkingDays = BUSINESS_DAYS_PER_MONTH) => {
  return Math.round(dailyRate * annualWorkingDays);
};

// Enhanced member search function
export const getFilteredRosterForRole = (itRoster, roleFunction, searchTerm = '', currentlyAssignedId = null) => {
  // Ensure we have a valid roster array
  if (!Array.isArray(itRoster)) {
    console.warn('getFilteredRosterForRole: itRoster is not an array:', itRoster);
    return [];
  }
  
  console.log(`getFilteredRosterForRole: Starting with ${itRoster.length} members, roleFunction: '${roleFunction}', searchTerm: '${searchTerm}', currentlyAssigned: '${currentlyAssignedId}'`);
  
  let filteredRoster = itRoster;
  
  // Apply search filter if search term is provided
  if (searchTerm && searchTerm.trim()) {
    const searchLower = searchTerm.toLowerCase().trim();
    filteredRoster = itRoster.filter(member => {
      if (!member) return false;
      
      const fullName = `${member.first_name || ''} ${member.last_name || ''}`.toLowerCase().trim();
      const name = (member.name || '').toLowerCase();
      const role = (member.role || '').toLowerCase();
      const functionName = (member.function || '').toLowerCase();
      const company = (member.company || '').toLowerCase();
      const location = (member.location || '').toLowerCase();
      
      const matches = fullName.includes(searchLower) ||
             name.includes(searchLower) ||
             role.includes(searchLower) ||
             functionName.includes(searchLower) ||
             company.includes(searchLower) ||
             location.includes(searchLower) ||
             (member.first_name || '').toLowerCase().includes(searchLower) ||
             (member.last_name || '').toLowerCase().includes(searchLower);
      
      return matches;
    });
    
    console.log(`getFilteredRosterForRole: After search filter: ${filteredRoster.length} members`);
  }
  
  // Always include the currently assigned member even if not in search results
  if (currentlyAssignedId) {
    const assignedMember = itRoster.find(member => member.id === currentlyAssignedId);
    if (assignedMember && !filteredRoster.find(member => member.id === currentlyAssignedId)) {
      filteredRoster = [assignedMember, ...filteredRoster];
    }
  }

  console.log(`getFilteredRosterForRole: Returning ${filteredRoster.length} members`);
  return filteredRoster;
}